import { SECURITY_NAME_ALIAS, TITLE_TO_ALIAS } from 'common/constants/cap-table/capTable';
import type { HighlightVariant } from 'components/FileViewer/pdf-viewer/highlight-layer/HighlightLayer.props';
import type { CitationReferenceData } from 'services/reference-generator/types';
import type { ReviewEvidenceGroup, ReviewItem, ReviewStatus } from 'store/useReviewStore';
import { finiteNumberOf, sameAiValue } from './aiNumericValue';
import { formatDisplayValue, formatDistinctDisplayPair } from './formatAiDisplayValue';
import { matchAiOption } from './resolveAiOptionValue';
import type { RowFormat } from '../../../../common/types/scalarSpreadsheet';
import { CapTableCellWithReferences } from '../components/captable-table/CaptableTable.props';

// Anything carrying a review flag - AIFieldMetadata today, a CitationReference,
// or a record with a `status` field. `status` (SCAL-8976) is authoritative when
// present; is_accepted is the legacy fallback (boolean | null | undefined).
type ReviewStatusValue = 'pending' | 'accepted' | 'dismissed';
type AcceptFlagged =
  | { status?: ReviewStatusValue | string | null; is_accepted?: boolean | null }
  | null
  | undefined;

// Reverse of TITLE_TO_ALIAS so an alias (e.g. 'issue_price') maps to its human
// label ('Original Issue Price'). Falls back to title-casing the alias.
const ALIAS_TO_TITLE: Record<string, string> = Object.fromEntries(
  Object.entries(TITLE_TO_ALIAS).map(([title, alias]) => [alias, title])
);

const fieldLabelFor = (alias: string): string =>
  ALIAS_TO_TITLE[alias] ?? alias.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/**
 * Display for a SUGGESTED (or baseline) value on the card. Select-backed cells
 * (cell.options - e.g. Underlying Security) resolve the value against their
 * options first (the SHARED resolver: by id, then name - exact, then
 * case-insensitive trimmed) so the card never shows a raw option ID; everything
 * else formats with the cell's own config via the shared formatDisplayValue
 * (which itself maps security-type ids to labels), so card and cell always read
 * the same.
 */
const suggestionDisplayFor = (cell: CapTableCellWithReferences, val: unknown): string => {
  if (val === null || val === undefined || val === '') {
    return '';
  }
  const c = cell as {
    dbType?: string;
    alias?: string;
    format?: RowFormat;
    gridType?: string;
    options?: { id?: unknown; name?: unknown }[];
  };
  return (
    matchAiOption(c.options, val)?.label
    ?? formatDisplayValue(val, c.dbType, c.alias, c.format, c.gridType)
  );
};

/**
 * Card HEADLINE value - the analyst reads it as "what am I accepting":
 *   - metadata-backed item: the SUGGESTED value (metadata.value), formatted
 *     with the cell's own config - never the current cell value;
 *   - Merge item: "baseline -> suggested" (both formatted), the same arrow
 *     idiom as the in-grid merge viewer, baseline preferring the durable
 *     previous_value snapshot;
 *   - citation-only items and value-less metadata entries: NO headline value.
 *     There is no structured AI suggestion (a citation's extracted text lives in
 *     citationReferences[].tokenReferences[].plain_text and is already shown as
 *     the Evidence quote), so headlining the live cell value would masquerade the
 *     user's own data as an AI finding. The card simply omits the value line.
 */
const headlineValueFor = (
  cell: CapTableCellWithReferences,
  valueChange: 'merge' | 'new' | undefined
): string => {
  const meta = cell.aiFieldMetadata;
  if (meta == null || meta.value === undefined) {
    return '';
  }
  const suggested = suggestionDisplayFor(cell, meta.value);
  if (valueChange !== 'merge') {
    return suggested;
  }
  const baseline = 'previous_value' in meta ? meta.previous_value : cell.value;
  let baselineDisplay = suggestionDisplayFor(cell, baseline);
  let suggestedDisplay = suggested;
  if (baselineDisplay === suggestedDisplay) {
    // A REAL difference smaller than the cell's display precision (the two
    // sides are different doubles yet format identically) must never read
    // "same -> same": extend both sides' fraction digits until they visibly
    // differ (formatDistinctDisplayPair caps at 15).
    const c = cell as { dbType?: string; alias?: string; format?: RowFormat; gridType?: string };
    [baselineDisplay, suggestedDisplay] = formatDistinctDisplayPair(
      baseline,
      meta.value,
      c.dbType,
      c.alias,
      c.format,
      c.gridType
    );
  }
  return `${baselineDisplay || 'N/A'} → ${suggestedDisplay}`;
};

/**
 * Compose the card title. Normally "securityLabel - fieldLabel - displayedValue",
 * but the trailing value is dropped when it equals the security label - the
 * Security-Name field's value IS the security, so "Series D - Security Name - Series D"
 * collapses to "Series D - Security Name".
 */
const titleFor = (securityLabel: string, fieldLabel: string, displayedValue: string): string => {
  const head = `${securityLabel} - ${fieldLabel}`;
  return displayedValue && displayedValue !== securityLabel ? `${head} - ${displayedValue}` : head;
};

/**
 * Normalize an AI field's review state to a review status. A field is:
 *   - 'dismissed' when the metadata is absent, OR status==='dismissed'
 *                 (soft decline keeps the entry but flags it - SCAL-8976)
 *   - 'accepted'  when status==='accepted' (or, legacy, is_accepted is true)
 *   - 'pending'   otherwise
 *
 * `status` (SCAL-8976, the seam left in slice 1) is authoritative when present;
 * is_accepted is the backward-compatible fallback.
 */
export const reviewStatusOf = (meta: AcceptFlagged): ReviewStatus => {
  if (meta == null) {
    return 'dismissed';
  }
  if (meta.status) {
    return meta.status === 'accepted' || meta.status === 'dismissed' ? meta.status : 'pending';
  }
  return meta.is_accepted ? 'accepted' : 'pending';
};

/**
 * Review status of a CITATION reference itself. NOT reviewStatusOf: for field
 * metadata a falsy is_accepted means pending, but the citation payload follows the
 * SCAL-8962 backfill mapping - true -> accepted, false -> AI-generated awaiting
 * review, null/undefined -> legacy/manual (trusted, so accepted). An explicit
 * `status` on the record, when a payload carries one, is authoritative.
 */
export const citationReviewStatus = (citation: AcceptFlagged): ReviewStatus => {
  if (citation?.status) {
    return citation.status === 'accepted' || citation.status === 'dismissed' ? citation.status : 'pending';
  }
  return citation?.is_accepted === false ? 'pending' : 'accepted';
};

/**
 * Whether a citation footnote was AI-created (SCAL-8969). `created_source`
 * (SCAL-9166) is the durable provenance signal and wins whenever the payload
 * carries it; older payloads (mixed-deploy window) fall back to generation_job
 * presence - a nullable FK (SET_NULL on job deletion), so it is only a stopgap,
 * never the shipping mechanism.
 */
export const isAiCreatedCitation = (
  citation: Pick<CitationReferenceData, 'created_source' | 'generation_job'> | null | undefined
): boolean =>
  citation?.created_source != null ? citation.created_source === 'ai' : citation?.generation_job != null;

/**
 * PDF highlight variant for a citation, derived from the citation's REVIEW STATUS -
 * never from how the user navigated to it (SCAL-8986). Only a citation still
 * pending review reads AI-purple (yellow fill + purple outline); accepted - and
 * dismissed, which is not normally navigable - citations highlight plain yellow.
 */
export const statusToHighlightVariant = (status: ReviewStatus): HighlightVariant =>
  status === 'pending' ? 'ai' : 'citation';

// ASSUMPTION (confirm against real data): per-cell evidence counts are -
//   citations      = number of CitationReference rows on the cell
//   aiExtractions  = total tokenReferences across the cell's citations (each token
//                    reference is one AI-extracted data point); this is the only
//                    >1-per-cell signal available today. If a dedicated extraction
//                    count surfaces later, swap it in here.
//   excel          = number of MappingReference rows (all are xlsx/sheet-sourced,
//                    identified by their `sheet_name`).
// Only groups with count > 0 are emitted, so a chip with no number never appears
// for this slice (kept optional in the type for forward compatibility).
const evidenceFor = (cell: CapTableCellWithReferences): ReviewEvidenceGroup[] => {
  const citations = cell.citationReferences?.length ?? 0;
  const aiExtractions =
    cell.citationReferences?.reduce((sum, c) => sum + (c.tokenReferences?.length ?? 0), 0) ?? 0;
  const excel = cell.mappingReferences?.length ?? 0;

  const groups: ReviewEvidenceGroup[] = [];
  if (citations > 0) {
    groups.push({ kind: 'citations', count: citations });
  }
  if (aiExtractions > 0) {
    groups.push({ kind: 'aiExtractions', count: aiExtractions });
  }
  if (excel > 0) {
    groups.push({ kind: 'excel', count: excel });
  }
  return groups;
};

/**
 * Merge/New chip derivation: how accepting the field suggestion would change the
 * cell's data. The previous side prefers the entry's durable previous_value
 * baseline (falling back to the live cell value for legacy metadata), then
 * mirrors conditions.tsx's String-compare:
 *   'merge'   = a previous value exists and differs (accept supersedes real data)
 *   'new'     = the cell is empty (the suggestion fills it)
 *   undefined = no field suggestion with a value (citation-only items), or the
 *               suggestion equals the current value (AI-created security cells
 *               carry their values already - nothing is superseded or filled).
 */
export const valueChangeFor = (
  cell: Pick<CapTableCellWithReferences, 'value' | 'aiFieldMetadata' | 'options'>
): 'merge' | 'new' | undefined => {
  const meta = cell.aiFieldMetadata;
  const aiValue = meta?.value;
  if (meta == null || aiValue === undefined) {
    return undefined;
  }
  // Previous side: prefer the durable review-trail baseline the backend stamps
  // on the entry (previous_value; null = the apply created the security, so
  // nothing pre-existed -> 'new'). The live cell value lies after successive
  // applies; it remains the fallback for legacy metadata without the key.
  const prev = 'previous_value' in meta ? meta.previous_value : cell.value;
  if (prev === null || prev === undefined || prev === '') {
    return 'new';
  }
  // Select-backed cells (cell.options - e.g. Underlying Security) store option
  // IDs while the AI suggests the option NAME: compare in RESOLVED space (raw
  // spellings when nothing resolves) so an echo suggestion is not a merge.
  // Numeric values compare in DOUBLE space (sameAiValue): two decimal spellings
  // of one double (shortest repr vs the column's 15dp expansion) are the same
  // value, not a merge.
  const resolve = (v: unknown) => matchAiOption(cell.options, v)?.id ?? v;
  return sameAiValue(resolve(aiValue), resolve(prev)) ? undefined : 'merge';
};

/**
 * Exact raw spellings for a NUMERIC merge: the card's expandable detail shows
 * the untruncated current/suggested strings so a difference smaller than the
 * headline's display precision stays auditable. Undefined for non-merge items
 * and non-numeric merges.
 */
const exactValuesFor = (
  cell: Pick<CapTableCellWithReferences, 'value' | 'aiFieldMetadata'>,
  valueChange: 'merge' | 'new' | undefined
): ReviewItem['exactValues'] => {
  const meta = cell.aiFieldMetadata;
  if (valueChange !== 'merge' || meta == null) {
    return undefined;
  }
  const prev = 'previous_value' in meta ? meta.previous_value : cell.value;
  if (finiteNumberOf(prev) === undefined || finiteNumberOf(meta.value) === undefined) {
    return undefined;
  }
  return { current: String(prev), suggested: String(meta.value) };
};

/**
 * Pick the citation to surface as the card's ACTIVE/primary one (SCAL-9162, and the
 * T15/T16 FE remainder for coexisting retained citations). Value-acceptance and
 * citation-acceptance are separable: an analyst can keep the AI value yet REJECT its
 * citation (soft-unlink -> is_accepted=false, via declineRefsForFields). So PREFER a
 * non-rejected citation (is_accepted !== false, i.e. pending or accepted) so a
 * rejected citation drops out of the card's doc-jump / source name while the value
 * (displayedValue) is untouched. Among the eligible set pick the MOST-RECENT by
 * created_at (retained prior-run citations coexist - newest wins), falling back to
 * source order when created_at is absent so the established "first citation" behavior
 * is preserved. When EVERY citation is rejected, fall back to the most-recent rejected
 * one so the card still points at a source rather than nothing.
 */
export const pickActiveCitation = (
  refs: CitationReferenceData[] | undefined
): CitationReferenceData | undefined => {
  if (!refs || refs.length === 0) {
    return undefined;
  }
  const live = refs.filter(ref => ref.is_accepted !== false);
  const pool = live.length > 0 ? live : refs;
  // String `>` on created_at assumes the backend emits consistent ISO-8601
  // (YYYY-MM-DDTHH:MM:SSZ); mixed formats would compare lexicographically wrong.
  return pool.reduce((best, ref) =>
    ref.created_at && (!best.created_at || ref.created_at > best.created_at) ? ref : best
  );
};

/**
 * Derive review cards from the already-parsed cap-table cells. Emits one item per
 * (securityId = columnId, fieldAlias = alias) for every cell carrying review
 * evidence: an AI field suggestion (pending AND accepted AND dismissed) OR any
 * CITATION - analyst-created included (SCAL-9159) - each tagged with its `status`
 * and provenance (`aiCreated`). Manual MAPPING-ONLY cells (Excel "=" mappings
 * with no citation) are excluded entirely (user-ratified noise cut). AI-suggested
 * fields keep their field-level review state; a cell listed only for its
 * citations takes the ACTIVE citation's own status (manual/legacy citations are
 * trusted -> accepted, AI citations awaiting review -> pending).
 *
 * The panel pins pending items on top and sorts reviewed/settled items below; within
 * each section this emission order is preserved, and it follows the cells' insertion
 * order - security by security, fields in the default cap-table row order - which is
 * the ticket's default ordering. The "needs review" count filters to pending only.
 *
 * A cell with NO ai metadata and NO citation is skipped: nothing to review.
 *
 * `documentNameById` (docId -> filename) is optional so this stays a pure,
 * easily-tested function; the wiring passes the DocumentFootnotesContext map.
 */
export const aggregateReviewItems = (
  cells: Record<string, CapTableCellWithReferences>,
  documentNameById?: Map<number, string>
): ReviewItem[] => {
  const all = Object.values(cells ?? {});

  // Per column, the security label is the value of the `name`-alias cell.
  const securityLabelByColumn = new Map<number | string, string>();
  all.forEach(c => {
    if (c.alias === SECURITY_NAME_ALIAS && c.columnId != null && c.value != null) {
      securityLabelByColumn.set(c.columnId, String(c.value));
    }
  });

  const items: ReviewItem[] = [];
  all.forEach(cell => {
    const { columnId, alias, aiFieldMetadata } = cell;
    // ACTIVE citation: backs the doc jump and, for citation-only items, the status +
    // provenance. A citation the analyst rejected (is_accepted=false) is skipped in
    // favor of a live / most-recent one (pickActiveCitation).
    const primaryCitation = pickActiveCitation(cell.citationReferences);
    const primaryMappingReference = cell.mappingReferences?.[0];
    // Panel eligibility: AI field suggestions and anything carrying a CITATION
    // (analyst-created included). Manual MAPPING-ONLY cells (Excel "=" cell
    // mappings with no citation) are trusted bookkeeping, not review material -
    // they flooded the panel with hundreds of noise cards, so they are excluded
    // entirely (user-ratified). A mapping ref still rides along on eligible
    // items for the Feature C xlsx jump.
    if (columnId == null || !alias || (aiFieldMetadata == null && !primaryCitation)) {
      return;
    }
    const status = aiFieldMetadata != null ? reviewStatusOf(aiFieldMetadata) : citationReviewStatus(primaryCitation);

    const securityId = Number(columnId);
    const securityLabel = securityLabelByColumn.get(columnId) ?? `Security ${securityId}`;
    const fieldLabel = fieldLabelFor(alias);
    const valueChange = valueChangeFor(cell);
    const displayedValue = headlineValueFor(cell, valueChange);
    const documentNames = documentNameById
      ? Array.from(
        new Set(
          [...(cell.citationReferences ?? []), ...(cell.mappingReferences ?? [])]
            .map(ref => documentNameById.get(ref.document_reference))
            .filter((n): n is string => Boolean(n))
        )
      )
      : [];

    items.push({
      id: `${securityId}:${alias}`,
      securityId,
      fieldAlias: alias,
      status,
      // Provenance (SCAL-9159): an AI field suggestion is always AI; reference-only
      // items derive it from the active citation (created_source, job fallback).
      aiCreated: aiFieldMetadata != null || isAiCreatedCitation(primaryCitation),
      // Accept/decline settle FIELD metadata, so only AI-suggested fields are
      // field-settleable (thumbs + bulk checkbox).
      canSettle: aiFieldMetadata != null,
      // Reference-only items with a document-backed ACTIVE citation settle at the
      // CITATION level instead (SCAL-8963): same thumbs, no bulk checkbox.
      canSettleCitation: aiFieldMetadata == null && primaryCitation != null,
      valueChange,
      // Echo-suggestion dedupe marker (auto-settled as accepted duplicate):
      // either settle surface may carry it.
      duplicate:
        aiFieldMetadata?.review_reason === 'duplicate' || primaryCitation?.review_reason === 'duplicate',
      securityLabel,
      fieldLabel,
      displayedValue,
      // Citation-only items: the CURRENT cell value the citation evidences, through
      // the same formatter as suggestions (options resolve to labels). Metadata-backed
      // items keep their suggestion headline instead, and a value-less METADATA entry
      // deliberately stays value-less (see headlineValueFor).
      citedValue: aiFieldMetadata == null ? suggestionDisplayFor(cell, cell.value) || undefined : undefined,
      // Numeric merges carry the exact raw spellings for the expandable detail
      // (sub-display-precision differences stay auditable at full precision).
      exactValues: exactValuesFor(cell, valueChange),
      title: titleFor(securityLabel, fieldLabel, displayedValue),
      evidence: evidenceFor(cell),
      commentary: aiFieldMetadata?.commentary ?? [],
      confidence: aiFieldMetadata?.confidence ?? null,
      citationTexts: (cell.citationReferences ?? []).flatMap(c =>
        (c.tokenReferences ?? []).map(t => t.plain_text).filter(Boolean)
      ),
      documentNames,
      // ACTIVE citation backs the slice-3 doc jump; PRIMARY Excel mapping ref backs
      // the Feature C xlsx reveal (undefined for citation-only items).
      primaryCitation,
      primaryMappingReference,
    });
  });

  return items;
};
